import {
  ApiTags,
  BillingAgreementDetails,
  GetNewPaymentPriceResponse,
  NewPaymentPriceResponse,
  GetPaymentLinkRequest,
  GroupData,
  PaymentDetails,
  PaymentLinkStatus,
  PaymentMethod,
  PlanDetails,
  SubscriptionType,
  ApiGeneralDataResponse,
  ApiResponse,
  UsageType,
  PlanUsageLimits,
  DisplayCurrencyDataPoint,
  GetUsageLimitsResponse,
  ApprovePaymentRequest,
  PaymentLinkData,
  CancellationReasonOption,
  SaveCancellationReasonRequest,
} from "@shared/types";
import { EndpointBuilder } from "@reduxjs/toolkit/query";
import moment from "moment";
import {
  TIME_RANGE_1_DAY,
  TIME_RANGE_1_MONTHS,
} from "@shared/common/constants";

export const settingsEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => ({
    getAllPlanDetails: build.query<
      PlanDetails[],
      { sub_group_id?: number; promo_code?: string | null } | void
    >({
      query: (args) => ({
        url: "get_all_plan_details/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: { plans: PlanDetails[] } }) =>
        response.data.plans,
    }),
    getAllPaymentMethods: build.query<
      PaymentMethod[],
      { sub_group_id: number } | void
    >({
      query: (args) => ({
        url: "get_all_payment_methods/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: {
        data: { payment_options: PaymentMethod[] };
      }) => response.data.payment_options,
    }),
    getPaymentLink: build.mutation<PaymentLinkData, GetPaymentLinkRequest>({
      query: (args) => ({
        url: "get_payment_link/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: PaymentLinkData }) => response.data,
    }),
    getPaymentStatus: build.mutation<PaymentLinkStatus, { invoice_id: string }>(
      {
        query: (args) => ({
          url: "get_payment_status/",
          method: "POST",
          body: args,
        }),
        transformResponse: (response: { data: PaymentLinkStatus }) =>
          response.data,
      }
    ),
    getRecentPayments: build.query<
      PaymentDetails[],
      { subscription_type: SubscriptionType }
    >({
      query: (args) => ({
        url: "get_recent_payments/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: {
        data: { recent_payments: PaymentDetails[] };
      }) => response.data.recent_payments,
      providesTags: () => [{ type: ApiTags.RECENT_PAYMENTS, id: "LIST" }],
    }),
    cancelSubscription: build.mutation<void, { sub_group_id: number } | void>({
      query: (args) => ({
        url: "cancel_subscription/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response, _, args) => {
        let tags = [{ type: ApiTags.RECENT_PAYMENTS, id: "LIST" }];
        if (args?.sub_group_id) {
          tags.push({ type: ApiTags.GROUPS, id: "LIST" });
        } else {
          tags.push({ type: ApiTags.USER, id: "LIST" });
        }
        return response ? tags : [];
      },
    }),
    getCancellationReasons: build.query<CancellationReasonOption[], void>({
      query: (args) => ({
        url: "get_cancellation_reasons/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<{ reasons: CancellationReasonOption[] }>
      ) => response.data.reasons,
    }),
    saveCancellationReason: build.mutation<void, SaveCancellationReasonRequest>(
      {
        query: (args) => ({
          url: "save_cancellation_reason/",
          method: "POST",
          body: args,
        }),
      }
    ),
    getCancelSubscriptionPromoCode: build.query<string, void>({
      query: (args) => ({
        url: "get_cancel_subscription_promo_code/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<{ promo_code: string }>) =>
        response.data.promo_code,
    }),
    getBillingAgreementDetails: build.query<
      BillingAgreementDetails,
      { payment_token: string }
    >({
      query: (args) => ({
        url: "get_billing_agreement_details/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: BillingAgreementDetails }) =>
        response.data,
    }),
    getGroupsData: build.query<GroupData[], void>({
      query: (args) => ({
        url: "get_groups_data/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: { data: GroupData[] } }) =>
        response.data.data,
      providesTags: () => [{ type: ApiTags.GROUPS, id: "LIST" }],
    }),
    getNewPaymentPrice: build.query<
      NewPaymentPriceResponse,
      Omit<GetPaymentLinkRequest, "email">
    >({
      query: (args) => ({
        url: "get_new_payment_price/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<GetNewPaymentPriceResponse>
      ) => ({
        ...response.data,
        message: response.message,
        code: response.code,
      }),
    }),
    getAllQuotaUsage: build.query<PlanUsageLimits, void>({
      query: () => ({
        url: "get_all_quota_usage/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<PlanUsageLimits>>
      ) => response.data.data,
    }),
    getQuotaUsage: build.query<number, { usage_type: UsageType }>({
      query: (args) => ({
        url: "get_quota_usage/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<number>>
      ) => response.data.data,
    }),
    getUsageLimits: build.query<
      GetUsageLimitsResponse,
      { is_demo_query?: boolean } | void
    >({
      query: (args) => ({
        url: "get_usage_limits/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<GetUsageLimitsResponse>>
      ) => {
        return response.data.data;
      },
    }),
    getTradeUsageChart: build.query<DisplayCurrencyDataPoint[], void>({
      query: () => ({
        url: "get_trade_usage_chart/",
        method: "POST",
        body: {
          time_range: TIME_RANGE_1_MONTHS,
          time_aggregation: TIME_RANGE_1_DAY,
          timezone_difference: moment().utcOffset(),
        },
      }),
      transformResponse: (
        response: ApiResponse<
          ApiGeneralDataResponse<DisplayCurrencyDataPoint[]>
        >
      ) => {
        return response.data.data;
      },
    }),
    approvePayment: build.mutation<void, ApprovePaymentRequest>({
      query: (args) => ({
        url: "approve_payment/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) =>
        response
          ? [
              { type: ApiTags.USER, id: "LIST" },
              { type: ApiTags.RECENT_PAYMENTS, id: "LIST" },
            ]
          : [],
    }),
    disconnectTelegramAccount: build.mutation<void, void>({
      query: () => ({
        url: "disconnect_telegram_account_confirmation_email/",
        method: "POST",
      }),
    }),
    disconnectDiscordAccount: build.mutation<void, void>({
      query: () => ({
        url: "disconnect_discord_account_confirmation_email/",
        method: "POST",
      }),
    }),
    getCurrentDiscount: build.query<number | undefined, void>({
      query: (args) => ({
        url: "get_current_discount/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data?: { discount: number } }) =>
        response.data?.discount,
    }),
  }),
};
